import { Link } from "react-router-dom";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import Reveal from "../../components/Reveal";

const FOCUS_AREAS = [
  ["Health Education", "Community talks and door-to-door engagement on preventable illness, hygiene, and when to seek professional care."],
  ["Workplace Safety Awareness", "Basic hazard-awareness sessions aimed at informal workers and small businesses who rarely get formal safety training."],
  ["Community Health Data Collection", "Gathering and analysing local health and accessibility data to identify where outreach is most needed."],
  ["Disability Inclusion Advocacy", "Pushing for accessible facilities and inclusive practice in community programs, informed directly by rehabilitation work."],
];

const TIMELINE = [
  ["2019", "Began volunteering in community health outreach alongside coursework, focused on hygiene education."],
  ["2021 – 2022", "Expanded into data collection and analysis, supporting local health workers with basic community health metrics."],
  ["2023 – Present", "Added disability inclusion advocacy to the portfolio, drawing directly on rehabilitation training to push for accessible community spaces and services."],
];

export default function ProjectVolunteerActivities() {
  return (
    <>
      <Nav />

      <div className="mx-auto max-w-content px-6 pt-32 md:px-12 md:pt-40">
        <Link to="/projects" className="text-[0.75rem] uppercase tracking-widest2 text-ink-faint hover:text-heading">
          &larr; Back to Projects
        </Link>
      </div>

      <Reveal className="mx-auto max-w-content px-6 pb-14 pt-8 md:px-12 md:pb-20">
        <p className="eyebrow">Volunteering &middot; Disability Inclusion</p>
        <h1 className="mt-3 font-serif text-4xl font-extrabold leading-tight text-heading md:text-5xl">
          EB Volunteer <em className="italic text-gold-500">Activities</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-[1rem] leading-relaxed text-ink-soft">
          Sustained engagement in community health and disability outreach, facilitating health education
          and workplace safety awareness, collecting and analysing community health data, and advocating
          for disability inclusion to strengthen community resilience.
        </p>
      </Reveal>

      <Reveal as="section" className="border-y border-line bg-surface-alt">
        <div className="mx-auto grid max-w-content grid-cols-2 divide-x divide-line px-6 sm:grid-cols-4 md:px-12">
          <div className="px-2 py-8 text-center first:pl-0 sm:px-6">
            <div className="font-serif text-3xl text-heading">2019</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Started</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">4</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Focus Areas</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">6+</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Years of Involvement</div>
          </div>
          <div className="px-2 py-8 text-center sm:px-6">
            <div className="font-serif text-3xl text-heading">Ongoing</div>
            <div className="mt-1 text-[0.75rem] uppercase tracking-widest2 text-ink-faint">Status</div>
          </div>
        </div>
      </Reveal>

      <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
        <div className="grid gap-14 md:grid-cols-[1fr_320px] md:gap-16">
          <div>
            <Reveal className="mb-16">
              <p className="eyebrow">Overview</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Why this work stayed with me</h2>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                This isn&rsquo;t a single project with a start and end date; it&rsquo;s a running commitment
                that shifted shape as I grew, from basic hygiene education to data collection to disability
                advocacy once my rehabilitation training gave me the vocabulary and standing to push for it.
              </p>
              <p className="mt-4 max-w-[62ch] text-[0.95rem] leading-relaxed text-ink-soft">
                Community resilience isn&rsquo;t built by any one intervention; it&rsquo;s built by showing up
                consistently, across health, safety, and accessibility, until the community can rely on it.
              </p>
            </Reveal>

            <Reveal className="mb-16">
              <p className="eyebrow">Focus Areas</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">Where the effort goes</h2>
              <div className="mt-6 divide-y divide-line border-t border-line">
                {FOCUS_AREAS.map(([title, desc]) => (
                  <div key={title} className="grid gap-1 py-5 md:grid-cols-[220px_1fr] md:gap-8">
                    <p className="font-serif text-base text-heading">{title}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal>
              <p className="eyebrow">Timeline</p>
              <h2 className="mt-2 font-serif text-2xl text-heading">How the focus evolved</h2>
              <div className="mt-6 space-y-6">
                {TIMELINE.map(([date, desc]) => (
                  <div key={date} className="grid gap-1 border-t border-line pt-5 md:grid-cols-[140px_1fr] md:gap-8">
                    <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-500">{date}</p>
                    <p className="text-[0.9rem] leading-relaxed text-ink-soft">{desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal as="aside" className="h-fit space-y-6 md:sticky md:top-28">
            <div className="border border-line-strong p-6">
              <p className="eyebrow mb-4">Project Info</p>
              <dl className="space-y-3 text-[0.85rem]">
                <div><dt className="text-ink-faint">Role</dt><dd className="text-heading">Volunteer &amp; Advocate</dd></div>
                <div><dt className="text-ink-faint">Type</dt><dd className="text-heading">Community Health &amp; Disability Outreach</dd></div>
                <div><dt className="text-ink-faint">Duration</dt><dd className="text-heading">2019 – Present</dd></div>
              </dl>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal as="section" className="bg-forest-900">
        <div className="mx-auto max-w-content px-6 py-16 md:px-12 md:py-24">
          <p className="text-[0.75rem] uppercase tracking-widest2 text-gold-300">Why it matters</p>
          <h2 className="mt-2 font-serif text-2xl font-extrabold text-white md:text-3xl">
            Consistency is the intervention
          </h2>
          <p className="mt-6 max-w-[62ch] text-[0.95rem] leading-relaxed text-forest-50/80">
            A single health talk changes little. Six years of showing up, adapting to what the community
            actually needs, and eventually being able to advocate for disability inclusion from a position
            of clinical training, that&rsquo;s what builds resilience that outlasts any one program.
          </p>
        </div>
      </Reveal>

      <section className="border-t border-line">
        <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-6 px-6 py-14 md:px-12">
          <p className="font-serif text-xl text-heading">See all projects</p>
          <Link
            to="/projects"
            className="border border-forest-900 bg-forest-900 px-6 py-3 text-[0.75rem] uppercase tracking-widest2 text-white transition-colors hover:bg-forest-700"
          >
            Browse Projects &rarr;
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}
